import './App.css';
import React, { Suspense, useEffect, useState } from 'react';
import AppRouter from './navigation';
import initI18n from './i18n';
import { useSelector, useDispatch } from 'react-redux';
import { store, resetAllStores } from './store/store';
import { queryClient } from './config/queryClient';
import { clearModuleCaches } from './utils/clearModuleCaches';
import i18next from 'i18next';
import moment from 'moment';
import 'moment/locale/fr';
import 'moment/locale/en-gb';
import { useLocation } from 'react-router-dom';

import VerificationModal from './components/common/VerificationModal';
import { auth } from './firebase';
import { useTranslation } from 'react-i18next';
import DebugPage from './components/common/DebugPage';
import RealtimeProvider from './components/common/RealtimeProvider';
import CustomizationsProvider from './components/common/CustomizationsProvider';
import BrandProvider from './components/common/BrandProvider';
import lazyWithRetry from './utils/lazyWithRetry';
import { isChunkLoadError, reloadAfterChunkError } from './utils/chunkLoadRecovery';
import NewVersionSnackbar from './components/common/NewVersionSnackbar';
import { isAgentAdminAllowed } from './agent/api/agentAdminClient';
import { openModal, closeModal } from './store/modalSlice';
import { guardOfflineLogout } from './offline/runtime';
import { traceLogout } from './utils/logoutTrace';
import useSessionExpiry from './hooks/useSessionExpiry';
import { isAddonBookingRoute } from './utils/deviceInfo';
import useDeviceSecurity from './hooks/useDeviceSecurity';
import { setSentryUser } from './config/sentry';

const DevInspector = lazyWithRetry(() => import('./components/common/DevInspector'));
const PricingBreakdown = lazyWithRetry(() => import('./components/common/PricingBreakdown'));

initI18n();

// Error Boundary Component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    // DOM mutation errors from external tools (antivirus, extensions) — recoverable
    if (error.name === 'NotFoundError' && error.message?.includes('removeChild')) {
      return { hasError: false, error };
    }
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // DOM mutation errors from external tools — silently ignore
    if (error.name === 'NotFoundError' && error.message?.includes('removeChild')) {
      console.warn('DOM mutation error caught by boundary (likely antivirus/extension) — ignored');
      return;
    }

    // Commit the fallback before recovery: React.lazy caches rejected imports.
    if (isChunkLoadError(error) && reloadAfterChunkError()) return;

    this.setState({ errorInfo });
    console.error('Error caught by boundary:', error, errorInfo);

    // Log pour le support
    if (window.gtag) {
      window.gtag('event', 'exception', {
        description: error.toString(),
        fatal: false
      });
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <DebugPage
          error={this.state.error}
          errorInfo={this.state.errorInfo}
          onRetry={() => this.setState({ hasError: false, error: null, errorInfo: null })}
          onRefresh={() => window.location.reload()}
        />
      );
    }

    return this.props.children;
  }
}

function App() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const location = useLocation();
  const lang = useSelector((state) => state.core.user?.lang || 'fr');
  const user = useSelector((state) => state.core.user);
  const sentryUserId = user?.uid;
  const sentryBusinessId = user?.activeBusiness?.businessId || user?.activeBusiness?.id;
  const sentryUserName = [user?.activeBusiness?.publicDisplay?.name,
    user?.activeBusiness?.displayName, user?.displayName]
    .find((value) => typeof value === 'string' && value.trim()) || '';
  const sentryUserEmail = user?.email;
  const sentryBusinessName = user?.activeBusiness?.businessName || user?.activeBusiness?.name;
  const sentryRole = user?.activeBusiness?.role;

  useEffect(() => {
    setSentryUser(sentryUserId, sentryBusinessId, {
      displayName: sentryUserName,
      email: sentryUserEmail,
      businessName: sentryBusinessName,
      role: sentryRole,
    });
  }, [sentryUserId, sentryBusinessId, sentryUserName, sentryUserEmail, sentryBusinessName, sentryRole]);
  const businessData = useSelector((state) => state.core.businessData);
  const mainElement = useSelector((state) => state.element?.mainElement);
  const businessModules = useSelector((state) => state.core.businessModules);
  const currentSection = useSelector((state) => state.core.currentSection);
  const isDemoMode = useSelector((state) => state.core.isDemoMode);
  const isModalOpen = useSelector((state) => state.modals.isOpen);
  const modalType = useSelector((state) => state.modals.modalType);

  const pathParts = location.pathname.split('/').filter(Boolean);
  const moduleName = pathParts[0] === 'bos' && pathParts[3] ? pathParts[3] : null;
  const currentModule = businessModules?.find((mod) => mod?.id === moduleName);
  const getBusinessId = () => localStorage.getItem('businessId');
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [verificationType, setVerificationType] = useState('email');
  const [showDevInspector, setShowDevInspector] = useState(false);
  const [showPricingBreakdown, setShowPricingBreakdown] = useState(false);
  // Load on first use, then keep its state and closing animation between openings.
  const [loadPricingBreakdown, setLoadPricingBreakdown] = useState(false);
  const windowPath = location.pathname;
  const isLoginPath = windowPath === '/';
  useDeviceSecurity({
    userId: user?.uid,
    enabled: !isDemoMode && !user?.uid?.startsWith('preview-') &&
      (windowPath === '/' || /^\/(bos|admin\/account|admin\/my-work|admin\/billing|admin\/apprentissage|checklist|pulse|aura|maestro|marketplace|desk|mcp)(\/|$)/.test(windowPath)),
  });


  // CMD+I / CTRL+I to toggle DevInspector (SUPER-ADMIN only)
  // useEffect(() => {
  //   const handleKeyDown = (e) => {
  //     if ((e.metaKey || e.ctrlKey) && e.key === 'i') {
  //       e.preventDefault();
  //       if (user?.activeBusiness?.role === 'SUPER-ADMIN') {
  //         setShowDevInspector((prev) => !prev);
  //       }
  //     }
  //   };
  //   window.addEventListener('keydown', handleKeyDown);
  //   return () => window.removeEventListener('keydown', handleKeyDown);
  // }, [user?.activeBusiness?.role]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key?.toLowerCase() === 'p' && !e.metaKey && !e.ctrlKey && !e.altKey && !e.shiftKey) {
        const tag = e.target?.tagName?.toLowerCase();
        if (tag === 'input' || tag === 'textarea' || tag === 'select' || e.target?.isContentEditable) return;
        if (!user?.uid || isLoginPath) return;
        if (!isAgentAdminAllowed(user)) return;
        e.preventDefault();
        setLoadPricingBreakdown(true);
        setShowPricingBreakdown((prev) => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [user?.uid, isLoginPath]);

  // CMD+K / CTRL+K to toggle command palette via Redux modal system
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        if (!user?.uid || isLoginPath) return;
        e.preventDefault();
        if (modalType === 'commandPalette') {
          dispatch(closeModal());
        } else if (!isModalOpen) {
          dispatch(openModal({ modalType: 'commandPalette' }));
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [user?.uid, isLoginPath, isModalOpen, modalType, dispatch]);

  useEffect(() => {
    if (lang && i18next.language !== lang) {
      i18next.changeLanguage(lang);
    }
    if (lang) {
      moment.locale(lang);
    }
  }, [lang]);



  // Update browser tab title dynamically
  useEffect(() => {
    // Public booking pages own their title using the requested business, not
    // the company/module left in the connected BOS session.
    if (isAddonBookingRoute(location.pathname) || location.pathname === '/demo' || location.pathname.startsWith('/demo/')) {
      return;
    }

    const elementName = mainElement?.elementName;
    const moduleLabelName = currentModule?.name;
    const businessName = businessData?.name;

    if (currentSection === 'ACCOUNT') {
      document.title = (t('accountSettings') || 'Account Settings') + ' - Node';
    }  else if (moduleLabelName) {
      document.title = moduleLabelName + ' - Node';
    }  else if (elementName) {
      document.title = elementName + ' - Node';
    } else if (businessName) {
      document.title = businessName + ' - Node';
    } else {
      document.title = 'Node';
    }
  }, [mainElement?.elementName, currentModule?.name, businessData?.name, currentSection, businessData?.id, location.pathname]);

  useEffect(() => {
    const handleOpenVerification = (event) => {
      if (user?.uid && !isDemoMode) {
        setVerificationType(event.detail?.type || 'email');
        setShowVerificationModal(true);
      }
    };

    window.addEventListener(
      'node:open-verification',
      handleOpenVerification
    );
    return () =>
      window.removeEventListener(
        'node:open-verification',
        handleOpenVerification
      );
  }, [user?.uid, isDemoMode]);



  const handleLogout = async (reason = 'user-logout', isCurrentSession = () => true) => {
    if (!await guardOfflineLogout() || !isCurrentSession()) return;
    traceLogout(reason, { userId: user?.uid || null });
    try {
      if (user?.uid) {
        localStorage.removeItem(`verificationDismissed_${user.uid}`);
      }
      store.dispatch(resetAllStores());
      queryClient.clear();
      clearModuleCaches();
      await auth.signOut();
      localStorage.removeItem('businessToken');
      localStorage.removeItem('businessesHooked');
      localStorage.removeItem('history');
      window.location.href = '/';
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const sessionExpiryEnabled = !isDemoMode && !isLoginPath && !isAddonBookingRoute(windowPath)
    && !['/doc', '/demo', '/onboarding', '/build', '/studio'].some((prefix) => windowPath.startsWith(prefix));
  useSessionExpiry({
    userId: user?.uid,
    businessId: localStorage.getItem('businessId') || businessData?.id,
    enabled: sessionExpiryEnabled,
    onLogout: handleLogout,
  });

  const handleDismissVerification = () => {
    setShowVerificationModal(false);
  };

  return (
    <ErrorBoundary>
        <RealtimeProvider>
          <CustomizationsProvider>
            <BrandProvider>
              {showVerificationModal && user && (
                <VerificationModal
                  user={user}
                  initialType={verificationType}
                  onClose={handleDismissVerification}
                />
              )}

              <AppRouter />

              <NewVersionSnackbar />
              {showDevInspector && (
                <Suspense fallback={null}>
                  <DevInspector
                    open={showDevInspector}
                    onClose={() => setShowDevInspector(false)}
                  />
                </Suspense>
              )}
              {loadPricingBreakdown && (
                <Suspense fallback={null}>
                  <PricingBreakdown
                    open={showPricingBreakdown}
                    onClose={() => setShowPricingBreakdown(false)}
                  />
                </Suspense>
              )}
            </BrandProvider>
          </CustomizationsProvider>
        </RealtimeProvider>
    </ErrorBoundary>
  );
}

export default App;
